import React from 'react';
import { CreditCard, Clock, Ban, Building2, RefreshCw, MessageSquare, AlertTriangle, BadgeIndianRupee, School } from 'lucide-react';
import PolicySectionCard from '@/components/policy/PolicySectionCard';
import PageHeader from '@/components/layout/PageHeader';
import PolicyLayout from '@/components/policy/PolicyLayout';
import { motion } from 'framer-motion';

const CancellationPolicy = () => {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 }
  };

  return (
    <PolicyLayout>
      <PageHeader
        title="Cancellation & Refund Policy"
        description="Last updated: April 11, 2025"
        icon={<BadgeIndianRupee className="w-8 h-8 text-primary" />}
      />

      {/* Cards Grid */}
      <motion.div 
        className="space-y-6"
        variants={container}
        initial="hidden"
        animate="show"
      >
        <motion.div variants={item}>
          <PolicySectionCard
            title="Cancellation Policy"
            icon={CreditCard}
            variant="indigo"
          >
            <p>We understand that circumstances may change. You can cancel your subscription at any time by accessing your account settings on the Edaptica platform.</p>
            
            <div className="mt-4 space-y-4">
              <h3 className="text-white font-semibold">1.1 Trial Period</h3>
              <p>We offer a 7-day trial for all new users. During this period, you can explore all the features of the platform at no cost.</p>
              <p>If you do not cancel your subscription before the trial ends, your selected plan will be activated, and you will be billed accordingly.</p>

              <h3 className="text-white font-semibold">1.2 After Trial Period</h3>
              <p>Once your subscription (Basic or Pro plan) is activated after the 7-day trial, no refunds will be issued under any circumstances. This includes:</p>
              <ul className="list-disc pl-6 mt-2 space-y-1">
                <li>Non-usage or inactivity</li>
                <li>Forgetting to cancel during the trial period</li>
                <li>Accidental purchases</li>
                <li>Change of mind</li>
              </ul>
            </div>
          </PolicySectionCard>
        </motion.div>

        <motion.div variants={item}>
          <PolicySectionCard
            title="No Refund Policy"
            icon={Ban}
          >
            <p>We provide full access to all features during the 7-day trial period so that you can evaluate our platform before committing. Therefore, we do not offer refunds after a subscription has been activated.</p>
            
            <p className="mt-4">This No Refund Policy applies to:</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              <li>Monthly and Annual Basic Plans</li>
              <li>Monthly and Annual Pro Plans</li>
              <li>Discounted or promotional plans</li>
              <li>All users who have not cancelled during the trial period</li>
            </ul>
          </PolicySectionCard>
        </motion.div>

        <motion.div variants={item}>
          <PolicySectionCard
            title="Refund Exceptions"
            icon={AlertTriangle}
          >
            <p>Refunds will only be considered under the following circumstances, evaluated on a case-by-case basis:</p>
            <ul className="list-disc pl-6 mt-4 space-y-1">
              <li>Duplicate payments or billing errors</li>
              <li>Severe technical issues preventing access, verified by our support team</li>
              <li>Unauthorized transactions proven with relevant evidence</li>
            </ul>
            <p className="mt-4">To request a refund under these exceptions, please contact:</p>
            <p className="mt-2">📧 support@edaptica.com with subject: "Refund Exception Request"</p>
          </PolicySectionCard>
        </motion.div>

        <motion.div variants={item}>
          <PolicySectionCard
            title="Educational Institutions, Government Bodies & Enterprise Clients"
            icon={School}
          >
            <p>For educational institutions, government bodies, and enterprise clients, our services and cancellation of services will be governed by separate mutual contract agreements with Cibeto Technologies Private Limited.</p>
            <p className="mt-4">These terms may differ from our standard policy and will be binding as per the mutually agreed-upon contract clauses.</p>
          </PolicySectionCard>
        </motion.div>

        <motion.div variants={item}>
          <PolicySectionCard
            title="Changes to This Policy"
            icon={RefreshCw}
          >
            <ul className="list-disc pl-6 space-y-2">
              <li>We reserve the right to update or change this Cancellation & Refund Policy at any time.</li>
              <li>Any changes will be effective immediately upon posting and the "Last updated" date will be revised.</li>
              <li>We will notify users of significant changes via email or platform notification.</li>
            </ul>
          </PolicySectionCard>
        </motion.div>

        <motion.div variants={item}>
          <PolicySectionCard
            title="Need Help?"
            icon={MessageSquare}
          >
            <p>If you have questions about this policy or need help with cancellations, please contact us:</p>
            <div className="mt-4 space-y-2">
              <p>📧 refunds@edaptica.com</p>
              <p>📞 +91-7992096907</p>
            </div>
          </PolicySectionCard>
        </motion.div>
      </motion.div>
    </PolicyLayout>
  );
};

export default CancellationPolicy;
